import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";
import type { QuotaTier, QuotaUsage } from "@/types";
import { buildIngestCardModel } from "@/utils/ingestCardModel";

const TIER_LABELS: Record<QuotaTier, string> = {
  spectator: "tierSpectator",
  rookie: "tierRookie",
  amateur: "tierAmateur",
  pro: "tierPro",
};

const STATE_STYLES = {
  onPace: {
    card: "bg-white border-secondary-200",
    badge: "bg-indigo-50 text-indigo-700",
    pct: "text-secondary-600",
    caption: "text-secondary-500",
  },
  overPace: {
    card: "bg-[#fffbeb] border-[#fcd34d]",
    badge: "bg-[#fef3c7] text-[#92400e]",
    pct: "text-[#b45309]",
    caption: "text-[#92400e]",
  },
  nearCap: {
    card: "bg-[#fef2f2] border-[#fca5a5]",
    badge: "bg-[#fee2e2] text-[#b91c1c]",
    pct: "text-[#b91c1c]",
    caption: "text-[#991b1b]",
  },
} as const;

interface IngestUsageCardProps {
  quota: QuotaUsage;
  /** Injectable clock for deterministic rendering/tests. Defaults to now. */
  now?: Date;
}

const SPECTATOR_SUNSET_WARNING_MS = 30 * 24 * 60 * 60 * 1000;

function SpectatorNotice({
  quota,
  t,
  now,
}: {
  quota: QuotaUsage;
  t: (key: string, options?: Record<string, unknown>) => string;
  now?: Date;
}) {
  const sunsetAt = quota.sunsetAt ? new Date(quota.sunsetAt) : null;
  const warnSunset =
    sunsetAt !== null &&
    sunsetAt.getTime() - (now ?? new Date()).getTime() <=
      SPECTATOR_SUNSET_WARNING_MS;

  return (
    <div
      className="rounded-lg border border-secondary-200 bg-white p-4 sm:p-5"
      data-state="readOnly"
    >
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-medium text-secondary-900">{t("title")}</h3>
        <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-secondary-600">
          {t(TIER_LABELS[quota.tier])}
        </span>
      </div>
      <p className="text-xs text-secondary-600">{t("readOnlyLine")}</p>
      {warnSunset && sunsetAt && (
        <p className="mt-1 text-xs font-medium text-[#b91c1c]">
          {t("sunsetWarning", {
            date: new Intl.DateTimeFormat(undefined, {
              month: "short",
              day: "numeric",
              timeZone: "UTC",
            }).format(sunsetAt),
          })}
        </p>
      )}
      {/* "See plans" CTA intentionally omitted while /plans is hidden
          (restore <Link to="/plans">{t("upgradeCta")}</Link> when billing ships) */}
    </div>
  );
}

export default function IngestUsageCard({ quota, now }: IngestUsageCardProps) {
  const { t, i18n } = useTranslation("quota");
  const location = useLocation();
  if (quota.tier === "spectator") {
    return <SpectatorNotice quota={quota} t={t} now={now} />;
  }
  const model = buildIngestCardModel(quota, now);
  const styles = STATE_STYLES[model.state];
  const resetDate = new Intl.DateTimeFormat(i18n.language, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(quota.period.end));

  return (
    <div className={`rounded-lg border p-4 sm:p-5 ${styles.card}`} data-state={model.state}>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-medium text-secondary-900">{t("title")}</h3>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${styles.badge}`}
        >
          {t(TIER_LABELS[quota.tier])}
        </span>
      </div>

      <div className="mb-1 flex justify-between text-xs text-secondary-600">
        <span>{t("usageOf", { used: model.usedDisplay, total: model.allowanceDisplay })}</span>
        <span className={`font-medium ${styles.pct}`}>{model.usagePct}%</span>
      </div>

      <Link
        to="/footage-usage"
        state={{ from: location.pathname }}
        aria-label={t("viewDetailsAria")}
        className="group block"
      >
        <div
          data-testid="ingest-bar"
          role="img"
          aria-label={t("barAria", { pct: model.usagePct })}
          className="flex h-2.5 overflow-hidden rounded-full transition group-hover:brightness-95"
        >
          {model.segments.map((segment, index) => (
            <div
              key={index}
              className={segment.className}
              style={{ width: `${segment.widthPct}%` }}
            />
          ))}
        </div>
        <span className="mt-1 flex items-center justify-end text-[10px] font-medium text-secondary-400 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 [@media(hover:none)]:opacity-100">
          {t("viewDetailsHint")}
          <ChevronRight className="ml-0.5 h-3 w-3" />
        </span>
      </Link>

      <div className={`mt-2 flex justify-between text-xs ${styles.caption}`}>
        <span>
          {model.state === "overPace" && t("overPaceCaption")}
          {model.state === "nearCap" &&
            t("remainingCaption", { hours: model.remainingDisplay })}
          {model.state === "onPace" &&
            `${t("uploadedLabel")} ${model.uploadedDisplay} · ${t("streamedLabel")} ${model.streamedDisplay}`}
        </span>
        <span>
          {t("dayOfPeriod", { n: model.dayOfPeriod, d: model.daysInPeriod })} ·{" "}
          {t("resets", { date: resetDate })}
        </span>
      </div>
    </div>
  );
}
